import { type FormEvent, useState } from 'react';
import { ApiRequestError } from '../api/client';
import {
  useDisableTwoFactor,
  useEnableTwoFactor,
  useMe,
  useRegenerateRecoveryCodes,
  useRevokeTrustedDevices,
  useSetupTwoFactor,
  useTwoFactorStatus,
} from '../api/queries';
import { PasswordForm } from '../components/PasswordForm';
import { QrCode } from '../components/QrCode';
import { ErrorState, LoadingBlock } from '../components/states';
import { formatDateTime } from '../format';
import { t } from '../i18n/pt-BR';

const tf = t.account.twoFactor;
const errorText = (e: unknown) =>
  e instanceof ApiRequestError ? e.message : e ? t.states.datasulDown : null;
const groups = (secret: string) => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

type Mode = 'idle' | 'enroll' | 'codes' | 'disable' | 'regenerate';

/** Segurança da própria conta: ativar/desativar o 2FA, códigos de recuperação e dispositivos confiáveis. */
export function SecurityPage() {
  const me = useMe();
  const status = useTwoFactorStatus();
  const setup = useSetupTwoFactor();
  const enable = useEnableTwoFactor();
  const disable = useDisableTwoFactor();
  const regenerate = useRegenerateRecoveryCodes();
  const revoke = useRevokeTrustedDevices();

  const [mode, setMode] = useState<Mode>('idle');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  if (status.isLoading) return <LoadingBlock />;
  if (status.error || !status.data)
    return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  const s = status.data;

  const reset = () => {
    setMode('idle');
    setCode('');
    setPassword('');
    setSaved(false);
    setCopied(false);
    setup.reset();
    enable.reset();
    disable.reset();
    regenerate.reset();
  };

  const startEnroll = () => {
    reset();
    setMode('enroll');
    setup.mutate();
  };

  const showCodes = (list: string[]) => {
    setCodes(list);
    setCode('');
    setPassword('');
    setMode('codes');
  };

  const onEnable = (e: FormEvent) => {
    e.preventDefault();
    enable.mutate({ codigo: code }, { onSuccess: (r) => showCodes(r['codigos-recuperacao']) });
  };
  const onDisable = (e: FormEvent) => {
    e.preventDefault();
    disable.mutate({ senha: password, codigo: code }, { onSuccess: reset });
  };
  const onRegenerate = (e: FormEvent) => {
    e.preventDefault();
    regenerate.mutate(
      { senha: password, codigo: code },
      { onSuccess: (r) => showCodes(r['codigos-recuperacao']) },
    );
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      /* sem permissão de área de transferência: o usuário copia à mão */
    }
  };

  return (
    <>
      {me.data?.['dois-fatores']['exige-configuracao'] ? (
        <div className="banner warn" role="alert">
          {t.account.required}
        </div>
      ) : null}

      <section className="panel" aria-labelledby="pw-title">
        <h2 id="pw-title">{t.password.title}</h2>
        <p className="hint">{t.password.description}</p>
        <PasswordForm />
      </section>

      <section className="panel" aria-labelledby="tf-title">
        <h2 id="tf-title">{tf.title}</h2>
        <p className="hint">{tf.description}</p>

        <p>
          <span className={`chip ${s.ativo ? 'ok' : 'warn'}`}>{s.ativo ? tf.statusOn : tf.statusOff}</span>{' '}
          {s.ativo && s['ativo-em'] ? (
            <span className="hint">{tf.since(formatDateTime(s['ativo-em']))}</span>
          ) : null}
        </p>
        {s['exigido-por-politica'] ? <p className="hint">{tf.requiredByPolicy}</p> : null}

        {/* ----- desativado ----- */}
        {!s.ativo && mode === 'idle' ? (
          <div>
            <button type="button" className="btn big" onClick={startEnroll}>
              {tf.enable}
            </button>
          </div>
        ) : null}

        {/* ----- ativação: QR + código ----- */}
        {mode === 'enroll' ? (
          <form onSubmit={onEnable} className="stack" noValidate>
            <strong>{tf.step1}</strong>
            {setup.isPending ? <LoadingBlock rows={2} /> : null}
            {setup.error ? <ErrorState error={setup.error} onRetry={startEnroll} /> : null}
            {setup.data ? (
              <div className="qr-row">
                <QrCode value={setup.data['url-otpauth']} label="QR code para o aplicativo autenticador" />
                <div>
                  <div className="hint">{tf.step1Manual}</div>
                  <code className="secret" data-testid="totp-secret">
                    {groups(setup.data.segredo)}
                  </code>
                </div>
              </div>
            ) : null}
            <strong>{tf.step2}</strong>
            <div className="field" style={{ maxWidth: 240 }}>
              <label htmlFor="enable-code">{t.auth.twoFactor.code}</label>
              <input
                id="enable-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{ letterSpacing: 4, fontFamily: 'var(--font-mono)', fontSize: 18 }}
              />
            </div>
            {errorText(enable.error) ? (
              <div className="form-error" role="alert">
                {errorText(enable.error)}
              </div>
            ) : null}
            <div className="row">
              <button
                type="submit"
                className="btn"
                disabled={enable.isPending || !setup.data || code.trim().length < 6}
              >
                {enable.isPending ? tf.working : tf.confirm}
              </button>
              <button type="button" className="btn ghost" onClick={reset}>
                {tf.cancel}
              </button>
            </div>
          </form>
        ) : null}

        {/* ----- códigos de recuperação (mostrados uma única vez) ----- */}
        {mode === 'codes' ? (
          <div className="stack">
            <strong>{tf.recoveryTitle}</strong>
            <p className="hint">{tf.recoveryText}</p>
            <ul className="codes" aria-label={tf.recoveryTitle}>
              {codes.map((c) => (
                <li key={c} className="mono">
                  {c}
                </li>
              ))}
            </ul>
            <div className="row">
              <button type="button" className="btn ghost" onClick={() => void copy()}>
                {copied ? tf.copied : tf.copy}
              </button>
            </div>
            <label className="check">
              <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
              <span>{tf.recoverySaved}</span>
            </label>
            <div>
              <button type="button" className="btn" disabled={!saved} onClick={reset}>
                {tf.done}
              </button>
            </div>
          </div>
        ) : null}

        {/* ----- ativado ----- */}
        {s.ativo && mode === 'idle' ? (
          <div className="stack">
            <p>
              {tf.codesLeft(s['codigos-restantes'])}
              {s['codigos-restantes'] <= 3 ? <span className="chip warn"> {tf.codesLow}</span> : null}
            </p>
            <p>{tf.devices(s['dispositivos-confiaveis'])}</p>
            <div className="row">
              <button type="button" className="btn ghost" onClick={() => setMode('regenerate')}>
                {tf.newCodes}
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={s['dispositivos-confiaveis'] === 0 || revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                {tf.revokeDevices}
              </button>
              {!s['exigido-por-politica'] ? (
                <button type="button" className="btn ghost" onClick={() => setMode('disable')}>
                  {tf.disable}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* ----- reautenticação: desativar / novos códigos ----- */}
        {mode === 'disable' || mode === 'regenerate' ? (
          <form onSubmit={mode === 'disable' ? onDisable : onRegenerate} className="stack" noValidate>
            <strong>{mode === 'disable' ? tf.disableTitle : tf.regenerateTitle}</strong>
            {mode === 'regenerate' ? <p className="hint">{tf.regenerateText}</p> : null}
            <div className="field" style={{ maxWidth: 320 }}>
              <label htmlFor="reauth-password">{tf.password}</label>
              <input
                id="reauth-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field" style={{ maxWidth: 320 }}>
              <label htmlFor="reauth-code">{tf.currentCode}</label>
              <input
                id="reauth-code"
                autoComplete="one-time-code"
                maxLength={20}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="mono"
              />
            </div>
            {errorText(disable.error ?? regenerate.error) ? (
              <div className="form-error" role="alert">
                {errorText(disable.error ?? regenerate.error)}
              </div>
            ) : null}
            <div className="row">
              <button
                type="submit"
                className="btn"
                disabled={disable.isPending || regenerate.isPending || !password || code.trim().length < 6}
              >
                {mode === 'disable' ? tf.disable : tf.newCodes}
              </button>
              <button type="button" className="btn ghost" onClick={reset}>
                {tf.cancel}
              </button>
            </div>
          </form>
        ) : null}
      </section>
    </>
  );
}
